import { ChangeProductDocument } from '@packages/gql/generated/shopping/ChangeProductDocument';
import type {
  AddBasketServiceLineitem,
  ChangeProductMutation,
} from '@packages/gql/generated/shopping/graphql';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { getBridge } from '@packages/shared/src/utils/getBridge';

/* GraphQL */ `
  mutation ChangeProduct(
    $locale: String!
    $sku: String!
    $lineItemId: String!
    $count: Int!
    $serviceLineitems: [AddBasketServiceLineitem!]
  ) {
    changeProduct(
      locale: $locale
      product: {
        sku: $sku
        lineItemId: $lineItemId
        count: $count
        serviceLineitems: $serviceLineitems
        basketOption: []
      }
    ) {
      lineItems {
        ...LineItemFragment
      }
      success
      messages {
        ...MessageFragment
      }
    }
  }
`;

type ChangeProductInBasketProps = {
  locale: string;
  sku: string;
  lineItemId: string;
  count: number;
  serviceLineitems?: AddBasketServiceLineitem[];
};
type ChangeProductInBasketResult =
  | { success: true; data: ChangeProductMutation['changeProduct'] }
  | { success: false; error?: Error };

export const useChangeProductInBasket = () => {
  const [{ data, fetching }, executeChangeProduct] = useAuthMutation(ChangeProductDocument);

  const changeProductInBasket = async (
    variables: ChangeProductInBasketProps,
  ): Promise<ChangeProductInBasketResult> => {
    const result = await executeChangeProduct(variables);

    if (result.error) {
      return {
        success: false,
        error: result.error,
      };
    }
    if (result.data && !result.data.changeProduct.success) {
      return {
        success: false,
        error: new Error('Unknown error occurred while changing product in basket (success false)'),
      };
    }
    if (result.data?.changeProduct) {
      getBridge().updateBasket();
      return {
        success: true,
        data: result.data.changeProduct,
      };
    }
    return {
      success: false,
      error: new Error('Unknown error occurred while changing product in basket'),
    };
  };

  return { data, fetching, changeProductInBasket };
};
